import Utils from "@/core/utils/Utils";

/**
 * Утилиты для работы с масками ввода.
 * Позволяет форматировать пользовательский ввод согласно заданной маске.
 */
class MaskUtils {
	/**
	 * Стандартные токены для масок ввода
	 */
	static readonly DEFAULT_TOKENS = {
		"#": { pattern: /\d/ },
		X: { pattern: /[0-9a-zA-Z]/ },
		S: { pattern: /[a-zA-Z]/ },
		A: { pattern: /[a-zA-Z]/, transform: (v: string) => v.toLocaleUpperCase() },
		a: { pattern: /[a-zA-Z]/, transform: (v: string) => v.toLocaleLowerCase() },
		"!": { escape: true },
	};
	/**
	 * Возвращает представление маски в виде строки с цифрами:
	 * - '0' - обычный символ (не токен)
	 * - '1' - экранированный токен (после символа '!')
	 * - '2' - токен маски
	 */
	static getBitMask(mask: string, tokens: any): string {
		let maskIndex = 0;
		let result = "";

		while (maskIndex < mask.length) {
			const token = tokens[mask[maskIndex]];

			if (typeof token === "undefined") {
				result += "0";
			} else {
				if (token.escape) {
					result += "1";
					maskIndex++;
				} else {
					result += "2";
				}
			}
			maskIndex++;
		}

		return result;
	}

	/**
	 * Удаляет из значения все символы, которые не являются токенами маски
	 */
	static deleteNotTokens(value: string, mask: string, tokens: any): string {
		// Собираем уникальные паттерны токенов
		const uniquePatterns: string[] = [];
		mask.split("").forEach((tokenName) => {
			const token = tokens[tokenName];
			if (Utils.isObject(token) && token.pattern instanceof RegExp) {
				const pattern = token.pattern.source;
				if (!uniquePatterns.includes(pattern)) {
					uniquePatterns.push(pattern);
				}
			}
		});

		// Находим символы, которые не соответствуют ни одному токену
		const trashSymbols: string[] = [];
		value
			.replaceAll(new RegExp(uniquePatterns.join("|"), "g"), "")
			.split("")
			.forEach((symbol) => {
				if (!trashSymbols.includes(symbol)) {
					trashSymbols.push(symbol);
				}
			});

		// Удаляем "мусорные" символы
		let result = value;
		if (trashSymbols.length > 0) {
			trashSymbols.forEach((symbol) => {
				result = result.replaceAll(symbol, "");
			});
		}

		return result;
	}

	/**
	 * Подсчитывает количество не-токенов от заданной позиции до ближайшего токена
	 */
	static notTokensCount(
		position: number,
		mask: string,
		tokens: any,
		forward: boolean = true
	): number {
		let skipChars = 0;
		let startPosition = position;
		const bitMask = this.getBitMask(mask, tokens);

		if (forward) {
			// Проверяем из начала в конец
			while (startPosition < mask.length) {
				const isToken = parseInt(bitMask[startPosition], 10) > 1;
				if (isToken) {
					break;
				} else {
					startPosition++;
					skipChars++;
				}
			}
		} else {
			while (startPosition > 0) {
				const isToken = parseInt(bitMask[startPosition], 10) > 1;
				if (isToken) {
					break;
				} else {
					startPosition--;
					skipChars--;
				}
			}
		}

		return skipChars;
	}

	/**
	 * Применяет маску к входной строке
	 */
	static maskAll(inputString: string | null, mask: string, tokens: any) {
		const result = {
			masked: "",
			raw: "",
			fullfilled: true,
		};

		if (inputString === null) {
			result.fullfilled = false;
			return result;
		}

		let value = this.deleteNotTokens(inputString, mask, tokens);
		mask = mask || "";
		let maskIndex = 0;
		let valueIndex = 0;

		while (maskIndex < mask.length) {
			let tokenSymbol = mask[maskIndex];
			const token = tokens[tokenSymbol];
			const valueSymbol = value[valueIndex];

			if (token && !token.escape) {
				if (token.pattern.test(valueSymbol)) {
					result.masked += token.transform ? token.transform(valueSymbol) : valueSymbol;
					result.raw += valueSymbol;
				} else {
					result.fullfilled = false;
					result.masked += "_";
				}
				valueIndex++;
			} else {
				if (token && token.escape) {
					maskIndex++; // берем следующий символ маски и обрабатываем его как обычный символ
					tokenSymbol = mask[maskIndex];
				}
				result.masked += tokenSymbol;
				if (valueSymbol === tokenSymbol) {
					valueIndex++; // пользователь ввел тот же символ
				}
			}
			maskIndex++;
		}

		// Обработка маски, которая заканчивается обычным символом: (#)
		let restOutput = "";
		while (maskIndex < mask.length) {
			const cMask = mask[maskIndex];
			if (tokens[cMask]) {
				restOutput = "";
				break;
			}
			restOutput += cMask;
			maskIndex++;
		}
		result.masked += restOutput;

		return result;
	}

	/**
	 * Возвращает индекс позиции курсора, где пользователь должен продолжить ввод.
	 * Используется для восстановления позиции курсора после прерванного ввода.
     *
     * UPD. Однако можно было бы сохранять индекс элемента маски на котором остановился ввод, и таким образом,
     * как будто бы проблема бы решилась без лишних циклов, но это не точно
	 */
	static getCurrentCursorPointer(masked: string, raw: string, bitMask: string): number {
		let cursorPointer = 0;

		for (
			let currentRawIndex = 0, currentMaskedIndex = 0;
			currentMaskedIndex < masked.length;

		) {
			if (
				raw[currentRawIndex] === masked[currentMaskedIndex] &&
				bitMask[currentMaskedIndex] === "2"
			) {
				cursorPointer++;
				currentRawIndex++;
				currentMaskedIndex++;
			} else if (["0", "1"].includes(bitMask[currentMaskedIndex])) {
				cursorPointer++;
				currentMaskedIndex++;
			} else {
				break;
			}
		}

		return cursorPointer;
	}
}

export default MaskUtils;